/**
 * TourTicketLink — on a tour sheet, the field ticket the sheet is on.
 *
 * The API's tour read carries no ticket id (measured at api d7f1f75f), so the
 * link is DERIVED from two reads the ticket roles already have:
 *
 *   1. GET /tickets?job_id=<the sheet's job>, then the one live (non-void)
 *      candidate: a per-day ticket whose work date is the sheet's work date, or
 *      the job's whole-job ticket.
 *   2. GET /tickets/:id for that candidate. The link shows ONLY when the
 *      ticket's `tours` lists this sheet with `releasedAt` empty — the API's own
 *      record that the sheet is on it. A candidate that does not list the sheet
 *      (a sheet approved after the draft, or one a refresh released) shows
 *      nothing rather than a guess.
 *
 * Rendered for the ticket READ roles only; the reads are not made otherwise.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useWsTicket, useWsTickets } from '../../../hooks/useWsTickets';
import { calendarDayString } from '../../../lib/calendar-date';
import type { WsTicket } from '../../../types/ws-tickets';
import { WsPanel } from '../WsBits';

const day = (v: string | null | undefined) => calendarDayString(v);

/** The live ticket a sheet could be on, by scope. Exported for the tests. */
export function tourTicketCandidate(tickets: WsTicket[], workDate: string | null): WsTicket | null {
  const live = tickets.filter((t) => t.status !== 'void');
  const wd = day(workDate);
  return (
    live.find((t) => t.scope === 'day' && wd !== null && day(t.workDate) === wd) ??
    live.find((t) => t.scope === 'job') ??
    null
  );
}

/** Whether a ticket's detail lists this sheet as on it (not released). */
export function ticketHoldsTour(ticket: WsTicket | null | undefined, tourId: string): boolean {
  return !!ticket?.tours?.some((t) => t.id === tourId && !t.releasedAt);
}

export function TourTicketLink({ tourId, jobId, workDate }: { tourId: string; jobId: string; workDate: string | null }) {
  const list = useWsTickets({ job_id: jobId });
  const candidate = tourTicketCandidate(list.data?.data ?? [], workDate);
  const detail = useWsTicket(candidate?.id);
  const ticket = detail.data?.data ?? null;
  if (!ticket || !ticketHoldsTour(ticket, tourId)) return null;
  return (
    <WsPanel eyebrow="Billing" title="Field ticket">
      <p className="px-4 py-3 text-sm text-fieldiq-text" data-testid="ws-tour-ticket">
        This tour sheet is on{' '}
        <Link to={`/well-servicing/tickets/${ticket.id}`} className="font-mono font-bold text-fi-amber hover:underline">
          {ticket.ticketNumber || '—'}
        </Link>
        .
      </p>
    </WsPanel>
  );
}
